import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Brain, X, Plus, ChatCircleDots, PencilSimple } from "@phosphor-icons/react";
import { ProBadge } from "@/components/ProBadge";
import api, { formatApiError } from "@/lib/api";

export default function CoachMemoryCard() {
  const [memories, setMemories] = useState(null);
  const [fact, setFact] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/coach/memories").then(({ data }) => setMemories(data.memories)).catch(() => setMemories([]));
  }, []);

  const add = async () => {
    if (!fact.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/coach/memories", { fact: fact.trim() });
      setMemories((m) => [data.memory, ...(m || [])]);
      setFact("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    setMemories((m) => m.filter((x) => x.memory_id !== id));
    try {
      await api.delete(`/coach/memories/${id}`);
    } catch {
      toast.error("Could not delete memory");
    }
  };

  return (
    <div className="clay p-6 sm:p-8" data-testid="coach-memory-card">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-2xl bg-brand/10 flex items-center justify-center">
          <Brain size={20} weight="fill" color="var(--brand)" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h3 className="font-display text-lg font-medium flex items-center gap-2">Coach memory <ProBadge testId="pro-badge-memory" /></h3>
          <p className="text-xs text-muted2">Durable things your coach remembers from your chats. You're in control — delete anything.</p>
        </div>
      </div>

      <div className="mt-5">
        {memories === null ? (
          <p className="text-sm text-muted2">Loading…</p>
        ) : memories.length === 0 ? (
          <p className="text-sm text-muted2" data-testid="memory-empty">
            Nothing yet — as you chat with your coach, it will remember the things that matter (preferences, constraints, life context).
          </p>
        ) : (
          <ul className="space-y-2" data-testid="memory-list">
            {memories.map((m) => (
              <li key={m.memory_id} data-testid={`memory-item-${m.memory_id}`}
                className="flex items-start gap-2.5 bg-secondary/60 rounded-2xl px-4 py-2.5">
                {m.source === "manual"
                  ? <PencilSimple size={14} color="var(--brand)" className="mt-1 shrink-0" />
                  : <ChatCircleDots size={14} color="var(--brand)" className="mt-1 shrink-0" />}
                <span className="text-sm text-ink/85 flex-1 leading-relaxed">{m.fact}</span>
                <button data-testid={`memory-delete-${m.memory_id}`} onClick={() => remove(m.memory_id)}
                  aria-label="Forget this" className="text-muted2 hover:text-caution transition-colors mt-0.5 shrink-0">
                  <X size={15} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex gap-2 mt-4">
        <input
          data-testid="memory-add-input"
          className="flex-1 bg-surface border border-border rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-brand transition-colors"
          placeholder="Tell your coach something to remember…"
          value={fact}
          maxLength={200}
          onChange={(e) => setFact(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <button
          data-testid="memory-add-btn"
          onClick={add}
          disabled={adding || !fact.trim()}
          className="shrink-0 inline-flex items-center gap-1.5 bg-ink text-bg rounded-full px-4 py-2 text-sm font-medium hover:bg-brand transition-colors disabled:opacity-50"
        >
          <Plus size={14} weight="bold" /> Remember
        </button>
      </div>
    </div>
  );
}
